/** Progress: the heaviest weight × reps set of each week, per lift, from an athlete's shared history. */
import { mondayOf } from "./days.js";
import { byName, learnKeys, norm, recordOf, working } from "./plan.js";
import { LB, isDistance, kilograms } from "./sets.js";
import { shownUnit } from "../prefs.js";
import { exerciseKey } from "./text.js";
import type { Exercise, HistoryWorkout } from "./types.js";

export interface ProgressPoint {
  week: string;
  value: number;
  /** The heaviest set's reps and day. */
  reps?: number;
  date?: string;
}

export interface LiftProgress {
  key: string;
  name: string;
  unit: string;
  last: string;
  points: ProgressPoint[];
}

export function progress(history: HistoryWorkout[] | null | undefined): LiftProgress[] {
  const lifts = new Map<string, { key: string; name: string; last: string; unit: string; weeks: Map<string, { kg: number; reps: number; date: string }> }>();
  for (const w of history ?? []) {
    const session = w.session ?? {};
    learnKeys(session);
    const exercises = new Map<string, Exercise>();
    for (const b of working(session.blocks)) for (const e of b.exercises ?? []) exercises.set(norm(e.id), e);
    for (const log of session.logs ?? []) {
      const ex = exercises.get(norm(log.exerciseID));
      if (log.weight == null || isDistance(log) || !(log.weight > 0)) continue;
      if (ex && recordOf(ex) !== "weightReps") continue;
      const key = log.exerciseKey || (ex ? exerciseKey(ex.name) : null);
      if (!key) continue;
      let lift = lifts.get(key);
      if (!lift) {
        lift = { key, name: ex ? ex.name : key, last: w.date, unit: log.unit || "kg", weeks: new Map() };
        lifts.set(key, lift);
      }
      if (w.date > lift.last) {
        lift.last = w.date;
        lift.unit = log.unit || "kg";
        if (ex) lift.name = ex.name;
      }
      const week = mondayOf(w.date);
      const kg = kilograms(log)!;
      const best = lift.weeks.get(week);
      const reps = typeof log.amount === "number" ? log.amount : 0;
      if (best == null || kg > best.kg || (kg === best.kg && reps > best.reps)) lift.weeks.set(week, { kg, reps, date: w.date });
    }
  }
  return [...lifts.values()]
    .map((lift) => {
      const unit = shownUnit(lift.unit);
      const factor = unit === "lb" ? LB : 1;
      const points = [...lift.weeks.keys()].sort().map((week) => {
        const best = lift.weeks.get(week)!;
        return { week, value: Math.round((best.kg / factor) * 100) / 100, reps: best.reps || undefined, date: best.date };
      });
      return { key: lift.key, name: lift.name, unit, last: lift.last, points };
    })
    .sort((a, b) => (a.last !== b.last ? (a.last > b.last ? -1 : 1) : byName(a, b)));
}
